/**
 * Intent and schema-based visualization inference.
 *
 * Produces chart specs compatible with `ChatCanvas` chart artifacts:
 * {
 *   chartType: "bar" | "line" | "scatter",
 *   data: [...],
 *   config: { title, xAxis, yAxis }
 * }
 */

const TIME_FIELD_HINTS = ['date', 'time', 'timestamp', 'month', 'year', 'season', 'week', 'game'];
const METRIC_FIELD_HINTS = [
  'count',
  'avg',
  'average',
  'pct',
  'percent',
  'rate',
  'points',
  'rebounds',
  'assists',
  'wins',
  'losses',
  'velocity',
  'spin',
  'era',
  'ops',
  'war',
  'score',
  'distance',
  'minutes',
  'usage',
];
const ID_FIELD_HINTS = ['id', '_id', 'uuid', 'guid', 'index'];

const TREND_QUERY_HINTS = ['trend', 'over time', 'history', 'progression', 'timeline', 'month', 'year', 'game by game'];
const RELATIONSHIP_QUERY_HINTS = ['correlation', 'relationship', 'vs', 'versus', 'against', 'compare x and y'];
const TOP_QUERY_HINTS = ['top', 'highest', 'best', 'most', 'leading'];
const BOTTOM_QUERY_HINTS = ['bottom', 'lowest', 'least', 'worst'];
const SHOT_CHART_QUERY_HINTS = ['shot chart', 'heat map', 'heatmap', 'zone', 'region'];
const DISTRIBUTION_QUERY_HINTS = ['distribution', 'breakdown', 'share', 'mix', 'composition'];
const COUNT_QUERY_HINTS = ['count', 'how many', 'frequency', 'volume'];

function toStringValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
}

function toLower(value) {
  return toStringValue(value).toLowerCase();
}

function isNumberLike(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return Number.isFinite(Number(trimmed.replace(/,/g, '')));
}

function toNumeric(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.replace(/,/g, '').trim();
  if (!normalized) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function isDateLike(value) {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return !Number.isNaN(Date.parse(trimmed));
}

function isLikelyIdField(fieldName) {
  const normalized = toLower(fieldName);
  return ID_FIELD_HINTS.some((hint) => normalized === hint || normalized.endsWith(hint));
}

function countMatches(values, matcher) {
  if (!Array.isArray(values) || values.length === 0) {
    return 0;
  }
  return values.reduce((count, value) => (matcher(value) ? count + 1 : count), 0);
}

function scoreFieldByHints(fieldName, hints) {
  const normalized = toLower(fieldName);
  let score = 0;
  hints.forEach((hint) => {
    if (normalized.includes(hint)) {
      score += 1;
    }
  });
  return score;
}

function analyzeColumns(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const sampleRows = rows.slice(0, 200);
  const fields = Object.keys(sampleRows[0] || {});

  return fields.map((field) => {
    const values = sampleRows.map((row) => row[field]).filter((value) => value !== null && value !== undefined && value !== '');
    const uniqueValues = new Set(values.map((value) => (typeof value === 'object' ? JSON.stringify(value) : String(value))));
    const total = values.length || 1;
    const numericRatio = countMatches(values, isNumberLike) / total;
    const dateRatio = countMatches(values, isDateLike) / total;

    let type = 'categorical';
    if (dateRatio >= 0.6 || scoreFieldByHints(field, TIME_FIELD_HINTS) > 0) {
      type = 'time';
    } else if (numericRatio >= 0.6) {
      type = 'numeric';
    }

    return {
      field,
      type,
      uniqueCount: uniqueValues.size,
      numericRatio,
      dateRatio,
      hintScore: scoreFieldByHints(field, METRIC_FIELD_HINTS),
      timeHintScore: scoreFieldByHints(field, TIME_FIELD_HINTS),
      idLike: isLikelyIdField(field),
    };
  });
}

function includesAny(text, fragments) {
  const normalized = toLower(text);
  return fragments.some((fragment) => normalized.includes(fragment));
}

function chooseMeasureField(columns, query) {
  const numeric = columns.filter((col) => col.type === 'numeric' && !col.idLike);
  if (numeric.length === 0) {
    return null;
  }

  return numeric
    .map((col) => {
      let score = col.hintScore * 2;
      if (includesAny(query, [toLower(col.field)])) {
        score += 2;
      }
      if (col.uniqueCount <= 1) {
        score -= 3;
      }
      if (col.uniqueCount > 200) {
        score -= 1;
      }
      return { col, score };
    })
    .sort((a, b) => b.score - a.score)[0]
    .col;
}

function chooseSecondaryMeasureField(columns, primaryField) {
  const numeric = columns
    .filter((col) => col.type === 'numeric' && !col.idLike && col.field !== primaryField)
    .sort((a, b) => b.hintScore - a.hintScore);
  return numeric[0] || null;
}

function chooseDimensionField(columns, query) {
  const timeFields = columns.filter((col) => col.type === 'time');
  const categorical = columns.filter((col) => col.type === 'categorical' && !col.idLike);

  const wantsTrend = includesAny(query, TREND_QUERY_HINTS);
  if (wantsTrend && timeFields.length > 0) {
    return timeFields.sort((a, b) => b.timeHintScore - a.timeHintScore)[0];
  }

  if (categorical.length > 0) {
    return categorical
      .map((col) => {
        let score = 0;
        if (includesAny(query, [toLower(col.field)])) {
          score += 2;
        }
        if (col.uniqueCount <= 25) {
          score += 1;
        }
        if (col.uniqueCount > 120) {
          score -= 2;
        }
        return { col, score };
      })
      .sort((a, b) => b.score - a.score)[0]
      .col;
  }

  if (timeFields.length > 0) {
    return timeFields[0];
  }

  return null;
}

function aggregateByDimension(rows, dimensionField, measureField) {
  const grouped = new Map();
  rows.forEach((row) => {
    const key = toStringValue(row[dimensionField]);
    const measure = toNumeric(row[measureField]);
    if (!key || measure === null) {
      return;
    }
    const existing = grouped.get(key) || 0;
    grouped.set(key, existing + measure);
  });
  return [...grouped.entries()].map(([dimension, value]) => ({
    [dimensionField]: dimension,
    [measureField]: value,
  }));
}

function countByDimension(rows, dimensionField, metricName = 'count') {
  const grouped = new Map();
  rows.forEach((row) => {
    const key = toStringValue(row[dimensionField]);
    if (!key) {
      return;
    }
    grouped.set(key, (grouped.get(key) || 0) + 1);
  });
  return [...grouped.entries()].map(([dimension, value]) => ({
    [dimensionField]: dimension,
    [metricName]: value,
  }));
}

function buildLineData(rows, xField, yField) {
  const mapped = rows
    .map((row) => ({
      [xField]: toStringValue(row[xField]),
      [yField]: toNumeric(row[yField]),
    }))
    .filter((row) => row[xField] && row[yField] !== null);

  return mapped
    .sort((a, b) => {
      const aTime = Date.parse(a[xField]);
      const bTime = Date.parse(b[xField]);
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
        return 0;
      }
      return aTime - bTime;
    })
    .slice(0, 40);
}

function buildBarData(rows, xField, yField, query) {
  const aggregated = aggregateByDimension(rows, xField, yField);
  const sorted = [...aggregated];

  if (includesAny(query, BOTTOM_QUERY_HINTS)) {
    sorted.sort((a, b) => (a[yField] || 0) - (b[yField] || 0));
  } else {
    sorted.sort((a, b) => (b[yField] || 0) - (a[yField] || 0));
  }

  return sorted.slice(0, includesAny(query, TOP_QUERY_HINTS) ? 15 : 20);
}

function buildScatterData(rows, xField, yField, labelField) {
  return rows
    .map((row) => ({
      [xField]: toNumeric(row[xField]),
      [yField]: toNumeric(row[yField]),
      label: labelField ? toStringValue(row[labelField]) : undefined,
    }))
    .filter((row) => row[xField] !== null && row[yField] !== null)
    .slice(0, 300);
}

function createTitle(query, chartType) {
  const cleanedQuery = toStringValue(query) || 'Query Result';
  if (chartType === 'line') {
    return `${cleanedQuery} (Trend)`;
  }
  if (chartType === 'scatter') {
    return `${cleanedQuery} (Relationship)`;
  }
  return cleanedQuery;
}

export function inferVisualizationFromQuery({ query, results, intent } = {}) {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const columns = analyzeColumns(results);
  if (columns.length === 0) {
    return null;
  }

  const queryText = toStringValue(query);
  const normalizedIntent = toLower(intent);
  const numericColumns = columns.filter((col) => col.type === 'numeric' && !col.idLike);
  const measure = chooseMeasureField(columns, queryText);
  const dimension = chooseDimensionField(columns, queryText);
  const secondaryMeasure = chooseSecondaryMeasureField(columns, measure?.field || null);
  const wantsRelationship = includesAny(queryText, RELATIONSHIP_QUERY_HINTS);
  const wantsTrend = includesAny(queryText, TREND_QUERY_HINTS);
  const wantsShotChart = includesAny(queryText, SHOT_CHART_QUERY_HINTS);
  const wantsDistribution = includesAny(queryText, DISTRIBUTION_QUERY_HINTS);
  const wantsCount = includesAny(queryText, COUNT_QUERY_HINTS);
  const intentPrefersViz = includesAny(normalizedIntent, ['visualization', 'chart', 'trend', 'compare']);

  let chartType = 'bar';
  let xField = dimension?.field || null;
  let yField = measure?.field || null;
  let chartData = [];

  if ((wantsRelationship || normalizedIntent === 'relationship') && numericColumns.length >= 2) {
    const xCandidate = measure || numericColumns[0];
    const yCandidate = secondaryMeasure || numericColumns.find((col) => col.field !== xCandidate.field);
    xField = xCandidate?.field || numericColumns[0].field;
    yField = yCandidate?.field || numericColumns[1].field;
    const labelField = columns.find((col) => col.type === 'categorical' && !col.idLike)?.field;
    chartType = 'scatter';
    chartData = buildScatterData(results, xField, yField, labelField);
  } else if ((wantsTrend || normalizedIntent === 'trend') && dimension?.type === 'time') {
    chartType = 'line';
    xField = dimension.field;
    if (!yField) {
      yField = 'count';
      chartData = buildLineData(countByDimension(results, xField, yField), xField, yField);
    } else {
      chartData = buildLineData(results, xField, yField);
    }
  } else if (wantsShotChart && xField) {
    chartType = 'bar';
    if (!yField) {
      yField = 'count';
      chartData = buildBarData(countByDimension(results, xField, yField), xField, yField, queryText);
    } else {
      chartData = buildBarData(results, xField, yField, queryText);
    }
  } else if (xField && yField) {
    chartType = 'bar';
    chartData = buildBarData(results, xField, yField, queryText);
  } else if (xField && (wantsDistribution || wantsCount || intentPrefersViz)) {
    chartType = 'bar';
    yField = 'count';
    chartData = buildBarData(countByDimension(results, xField, yField), xField, yField, queryText);
  }

  if (!xField || !yField || chartData.length === 0) {
    return null;
  }

  return {
    chartType,
    data: chartData,
    config: {
      title: createTitle(queryText, chartType),
      xAxis: xField,
      yAxis: yField,
      xLabel: xField,
      yLabel: yField,
    },
    inferred: {
      reason: chartType === 'line'
        ? 'Detected a time-series dimension'
        : chartType === 'scatter'
          ? 'Detected comparison/correlation intent with two numeric measures'
          : 'Detected categorical comparison intent',
      columns: columns.map((col) => ({ field: col.field, type: col.type })),
      intent: intent || null,
    },
  };
}

export default {
  inferVisualizationFromQuery,
};
